import { useCallback, useEffect, useState } from "react";
import {
  FormAlert,
  FormCard,
  FormField,
  FormGrid,
  FormSection,
} from "@NP-Webber/crm-kit";
import { ezcountApi, type EzcountConnectionStatus } from "../../api/ezcountApi";
import { toClientApiError } from "../../api/httpClient";

function statusAlert(status: EzcountConnectionStatus | null): {
  severity: "info" | "success" | "warning";
  text: string;
} {
  if (!status?.configured) {
    return { severity: "info", text: "טרם הוגדר" };
  }
  const keyPart = status.hasApiKey
    ? ` · מפתח: ${status.apiKeyMasked || "****"}`
    : "";
  if (status.isEnabled) {
    return { severity: "success", text: `מחובר ופעיל${keyPart}` };
  }
  return { severity: "warning", text: `מוגדר אך כבוי${keyPart}` };
}

export function EzcountConnectionPanel() {
  const [status, setStatus] = useState<EzcountConnectionStatus | null>(null);
  const [isEnabled, setIsEnabled] = useState(false);
  const [environment, setEnvironment] = useState<"demo" | "prod">("demo");
  const [apiKey, setApiKey] = useState("");
  const [apiEmail, setApiEmail] = useState("");
  const [developerEmail, setDeveloperEmail] = useState("");
  const [developerPhone, setDeveloperPhone] = useState("");
  const [defaultDocType, setDefaultDocType] = useState(405);
  const [autoIssueOnIncome, setAutoIssueOnIncome] = useState(false);
  const [dontSendEmail, setDontSendEmail] = useState(false);
  const [sendCopy, setSendCopy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const item = await ezcountApi.getConnection();
      setStatus(item);
      setIsEnabled(item.isEnabled);
      setEnvironment(item.environment);
      setApiEmail(item.apiEmail ?? "");
      setDeveloperEmail(item.developerEmail ?? "");
      setDeveloperPhone(item.developerPhone ?? "");
      setDefaultDocType(item.defaultDocType);
      setAutoIssueOnIncome(item.autoIssueOnIncome);
      setDontSendEmail(item.dontSendEmail);
      setSendCopy(item.sendCopy);
      setApiKey("");
    } catch (err) {
      setError(toClientApiError(err).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function runAction(fn: () => Promise<unknown>, okMessage: string) {
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      await fn();
      setSuccess(okMessage);
      await load();
    } catch (err) {
      setError(toClientApiError(err).message);
    } finally {
      setBusy(false);
    }
  }

  const docOptions = (status?.docTypeOptions ?? []).map((opt) => ({
    value: String(opt.value),
    label: `${opt.label} (${opt.value})`,
  }));

  const alert = statusAlert(status);
  const canSave = Boolean(status?.hasApiKey || apiKey.trim());
  const canTest = Boolean(status?.hasApiKey);

  if (loading) {
    return (
      <section
        className="settings-kit-panel"
        aria-label="איזיקאונט — חיבור והנפקת מסמכים"
      >
        <FormAlert severity="info">טוען חיבור…</FormAlert>
      </section>
    );
  }

  return (
    <section
      className="settings-kit-panel"
      aria-label="איזיקאונט — חיבור והנפקת מסמכים"
    >
      <FormCard
        title="איזיקאונט — חיבור והנפקת מסמכים"
        info="חיבור חד-פעמי ברמת הארגון להנפקת קבלות ומסמכי הנה״ח. לקוחות (כולל קופות) אינם מחזיקים חשבון איזיקאונט נפרד."
        error={error}
        success={success}
        onSave={() =>
          void runAction(
            () =>
              ezcountApi.saveConnection({
                isEnabled,
                environment,
                apiKey: apiKey.trim() || undefined,
                apiEmail: apiEmail.trim() || null,
                developerEmail: developerEmail.trim() || null,
                developerPhone: developerPhone.trim() || null,
                defaultDocType,
                autoIssueOnIncome,
                dontSendEmail,
                sendCopy,
              }),
            "החיבור נשמר",
          )
        }
        saveLabel="שמירת חיבור"
        saving={busy}
        saveDisabled={!canSave}
        onCancel={
          canTest
            ? () =>
                void runAction(
                  () => ezcountApi.testConnection(),
                  "בדיקת חיבור הצליחה (הונפק מסמך בדיקה)",
                )
            : undefined
        }
        cancelLabel="בדיקת חיבור"
      >
        <FormSection title="סטטוס">
          <FormAlert severity={alert.severity}>{alert.text}</FormAlert>
        </FormSection>

        <FormSection title="פרטי חיבור">
          <FormGrid cols={2}>
            <FormField
              label="סביבה"
              type="select"
              value={environment}
              disabled={busy}
              onChange={(v) => setEnvironment(v === "prod" ? "prod" : "demo")}
              options={[
                { value: "demo", label: "Demo" },
                { value: "prod", label: "ייצור (Prod)" },
              ]}
            />
            <FormField
              label="סוג מסמך ברירת מחדל"
              type="select"
              value={String(defaultDocType)}
              disabled={busy}
              onChange={(v) => setDefaultDocType(Number(v))}
              options={docOptions}
            />
            <FormField
              label={
                status?.hasApiKey
                  ? `מפתח API (${status.apiKeyMasked || "****"})`
                  : "מפתח API"
              }
              type="password"
              value={apiKey}
              onChange={setApiKey}
              placeholder={status?.hasApiKey ? "השאירו ריק כדי לא לשנות" : ""}
              disabled={busy}
            />
            <FormField
              label="אימייל חשבון EZcount (אופציונלי)"
              value={apiEmail}
              onChange={setApiEmail}
              disabled={busy}
            />
            <FormField
              label="אימייל מפתח (developer_email)"
              value={developerEmail}
              onChange={setDeveloperEmail}
              disabled={busy}
              required
            />
            <FormField
              label="טלפון מפתח (אופציונלי)"
              value={developerPhone}
              onChange={setDeveloperPhone}
              disabled={busy}
            />
          </FormGrid>
        </FormSection>

        <FormSection title="הגדרות הנפקה">
          <FormGrid cols={2}>
            <FormField
              checkbox
              label="הפעל הנפקת מסמכים לארגון"
              checked={isEnabled}
              disabled={busy}
              onChange={
                ((v: boolean) => setIsEnabled(v)) as unknown as (
                  value: string,
                ) => void
              }
            />
            <FormField
              checkbox
              label="הנפק קבלה אוטומטית ביצירת הכנסה"
              checked={autoIssueOnIncome}
              disabled={busy}
              onChange={
                ((v: boolean) => setAutoIssueOnIncome(v)) as unknown as (
                  value: string,
                ) => void
              }
            />
            <FormField
              checkbox
              label="אל תשלח מייל מלקוח EZcount"
              checked={dontSendEmail}
              disabled={busy}
              onChange={
                ((v: boolean) => setDontSendEmail(v)) as unknown as (
                  value: string,
                ) => void
              }
            />
            <FormField
              checkbox
              label="שלח עותק למערכת"
              checked={sendCopy}
              disabled={busy}
              onChange={
                ((v: boolean) => setSendCopy(v)) as unknown as (
                  value: string,
                ) => void
              }
            />
          </FormGrid>
        </FormSection>
      </FormCard>
    </section>
  );
}
